/**
 *  Copyright (C) 2022 - Jeci SARL - https://jeci.fr
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU Affero General Public License as
 *  published by the Free Software Foundation, either version 3 of the
 *  License, or (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU Affero General Public License for more details.
 *
 *  You should have received a copy of the GNU Affero General Public License
 *  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import { defineStore } from "pinia";
import alfrescoFileService from "@/services/AlfrescoFileService.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import errorService from "@/services/ErrorService.js";
import UploadWorker from "../workers/upload.worker.js?worker&inline";
import axios from "axios";

const beforeUnloadHandler = (event) => {
  event.preventDefault();
  event.returnValue =
    "Des téléchargements sont en cours et seront annulés si vous quittez la page.";
};

export const useUploadStore = defineStore("upload", {
  state: () => ({
    isUploading: false,
    files: [],
    progress: { completed: 0, total: 0 },
    worker: null,
    activeRequests: {},
    classificationPlanFiles: [],
    onCompleteCallback: null,
    pendingNodes: [],
    ghostContext: null,
  }),
  getters: {
    overallMessage(state) {
      const total = state.progress.total;
      if (total === 0) {
        return "";
      }

      const rejected = state.files.filter(
        (f) => f.status === "rejected",
      ).length;
      const errors = state.files.filter((f) => f.status === "error").length;

      if (rejected === state.files.length) {
        return "Import annulé";
      }

      if (state.isUploading) {
        return `Importation... (${state.progress.completed} / ${total})`;
      }

      if (errors > 0) {
        return `Terminé avec ${errors} erreur(s)`;
      }

      return "Import terminé";
    },
  },
  actions: {
    startUpload(uploadItems, nodeId) {
      console.log(`Attempting to start upload for node ${nodeId}.`);
      console.log(`Current isUploading status: ${this.isUploading}`);
      if (this.isUploading) {
        return;
      }

      this.initializeWorker();

      this.isUploading = true;
      this.progress = { completed: 0, total: uploadItems.length };

      this.files = uploadItems.map((f) => ({
        name: f.file.name,
        status: "pending",
        message: "",
        percent: 0,
      }));

      // Create ghost nodes immediately for all files so they appear
      // at once, before the sequential upload starts.
      for (const item of uploadItems) {
        const file = item.file;
        this.addGhostNode({
          nodeId: null,
          _originalFileName: file.name,
          name: file.name,
          isFile: true,
          isFolder: false,
          content: { mimeType: file.type, sizeInBytes: file.size },
          properties: {},
          _ghost: true,
          _uploadedAt: Date.now(),
          _uploadContext: {
            parentNodeId: nodeId,
            siteId: null,
            activeViewId: null,
            activeViewConfig: null,
          },
        });
      }

      window.addEventListener("beforeunload", beforeUnloadHandler);

      this.worker.postMessage({
        type: "start",
        payload: {
          files: uploadItems,
          nodeId: nodeId,
        },
      });
    },

    initializeWorker() {
      if (this.worker) {
        return;
      }

      this.worker = new UploadWorker();

      this.worker.onmessage = async ({ data }) => {
        const { type, payload } = data;
        switch (type) {
          case "requestUpload":
            await this.handleUploadRequest(payload.item, payload.nodeId);
            break;

          case "requestClassificationPlanUpload":
            await this.handleClassificationPlanUpload(payload.item);
            break;

          case "progress":
            this.progress = payload;
            break;

          case "complete":
            this.isUploading = false;
            window.removeEventListener("beforeunload", beforeUnloadHandler);
            console.debug("Lot terminé. Résultats:", payload);
            // Clean up classification plan files after upload
            this.classificationPlanFiles = [];
            if (this.onCompleteCallback) {
              this.onCompleteCallback();
              this.onCompleteCallback = null;
            }
            break;
        }
      };
    },

    async handleUploadRequest(item, nodeId) {
      const file = item.file;

      const fileState = this.files.find((f) => f.name === file.name);
      if (fileState) {
        fileState.status = "uploading";
      }

      const relativePath = item.relativePath || file.webkitRelativePath || "";
      const folderPath = relativePath.split("/").slice(0, -1).join("/");

      let opts;
      if (folderPath) {
        opts = {
          name: file.name,
          nodeType: "cm:content",
          renditions: "doclib",
          relativePath: relativePath,
        };
      } else {
        opts = {
          name: file.name,
          nodeType: "cm:content",
          renditions: "doclib",
        };
      }
      try {
        const request = alfrescoFileService.uploadFileWithProgress(
          nodeId,
          opts,
          file,
        );
        this.activeRequests[file.name] = request;

        const response = await request;
        delete this.activeRequests[file.name];

        if (fileState) {
          fileState.status = "success";
          fileState.percent = 100;
          fileState.id = response.entry.id;
          fileState.content = response.entry.content;
          fileState.isFile = response.entry.isFile;
          fileState.isFolder = response.entry.isFolder;
        }

        // Update the pre-created ghost with the real node ID and
        // response data so dedup can match it against entries later.
        this.updateGhostAfterUpload(file.name, {
          nodeId: response.entry.id,
          name: response.entry.name,
          isFile: response.entry.isFile,
          isFolder: response.entry.isFolder,
          content: response.entry.content,
          properties: response.entry.properties || {},
        });

        this.worker.postMessage({
          type: "uploadResult",
          payload: {
            status: "fulfilled",
            value: response,
            fileName: file.name,
          },
        });
      } catch (error) {
        delete this.activeRequests[file.name];

        // Remove the pre-created ghost on upload failure
        this.removeGhostByTempKey(file.name);

        let parsedError;
        try {
          parsedError = errorService.uploadFileError(error, file.name);
        } catch (e) {
          parsedError = e;
        }

        if (fileState) {
          fileState.status = "error";
          fileState.message = parsedError.message || "Erreur inconnue";
        }

        this.worker.postMessage({
          type: "uploadResult",
          payload: {
            status: "rejected",
            reason: parsedError,
            fileName: file.name,
          },
        });
      }
    },

    cancelUpload(fileName) {
      const request = this.activeRequests[fileName];
      if (request && typeof request.abort === "function") {
        request.abort(); // ← superagent annulation
        delete this.activeRequests[fileName];

        // Remove the pre-created ghost on cancellation
        this.removeGhostByTempKey(fileName);

        const file = this.files.find((f) => f.name === fileName);
        if (file) {
          file.status = "rejected";
          file.message = "Upload annulé";
        }

        this.worker?.postMessage({
          type: "uploadResult",
          payload: {
            status: "rejected",
            reason: new Error("Annulé"),
            fileName,
          },
        });
      }
    },

    clearCompleted() {
      if (!this.isUploading) {
        this.files = [];
        this.progress = { completed: 0, total: 0 };
      }
    },

    setGhostContext(context) {
      this.ghostContext = context;
    },

    addGhostNode(ghostData) {
      if (this.ghostContext) {
        Object.assign(ghostData._uploadContext, {
          siteId: this.ghostContext.siteId ?? ghostData._uploadContext.siteId,
          activeViewId:
            this.ghostContext.activeViewId ??
            ghostData._uploadContext.activeViewId,
          activeViewConfig:
            this.ghostContext.activeViewConfig ??
            ghostData._uploadContext.activeViewConfig,
          parentNodeId:
            this.ghostContext.parentNodeId ??
            ghostData._uploadContext.parentNodeId,
        });
      }
      this.pendingNodes.push(ghostData);
    },

    removeGhostNode(nodeId) {
      this.pendingNodes = this.pendingNodes.filter((g) => g.nodeId !== nodeId);
    },

    /**
     * Update a pre-created ghost with real data after upload success.
     * Finds the ghost by _originalFileName (stored at creation time).
     */
    updateGhostAfterUpload(fileName, data) {
      const ghost = this.pendingNodes.find(
        (g) => g.nodeId === null && g._originalFileName === fileName,
      );
      if (ghost) {
        Object.assign(ghost, data);
      }
    },

    /**
     * Remove a pre-created ghost on upload failure.
     */
    removeGhostByTempKey(fileName) {
      const idx = this.pendingNodes.findIndex(
        (g) => g.nodeId === null && g._originalFileName === fileName,
      );
      if (idx !== -1) {
        this.pendingNodes.splice(idx, 1);
      }
    },

    cleanExpiredGhosts(ttl = 300000) {
      const now = Date.now();
      this.pendingNodes = this.pendingNodes.filter(
        (g) => now - g._uploadedAt < ttl,
      );
    },

    /**
     * Start upload with classification plan
     * Uses the injector webscript to upload files with metadata
     * @param {string} workspaceSiteId - Site ID of the workspace
     * @param {string} pathFull - Full path within the site
     * @param {Array} files - Array of File objects to upload
     * @param {Object} metadata - Metadata properties to inject
     * @param {Array} fileRenames - Array of {original, new} rename mappings
     */
    startUploadWithClassificationPlan(
      workspaceSiteId,
      pathFull,
      files,
      metadata,
      fileRenames,
      destinationNodeId,
    ) {
      console.log(
        `Attempting to start classification plan upload for ${files.length} files.`,
      );
      console.log(`Current isUploading status: ${this.isUploading}`);
      if (this.isUploading) {
        return;
      }

      this.initializeWorker();

      this.isUploading = true;
      this.progress = { completed: 0, total: files.length };

      this.files = files.map((f) => ({
        name: f.name,
        status: "pending",
        message: "",
        percent: 0,
      }));

      window.addEventListener("beforeunload", beforeUnloadHandler);

      // Store files locally and create lightweight items for worker
      // Files cannot be cloned for postMessage, so we keep them here
      this.classificationPlanFiles = files.map((file, index) => ({
        file,
        workspaceSiteId,
        pathFull,
        metadata,
        fileRenames,
        destinationNodeId,
        index,
      }));

      // Create ghost nodes immediately for all files
      for (const file of files) {
        const renameInfo = fileRenames?.find((r) => r.original === file.name);
        const finalName = renameInfo ? renameInfo.new : file.name;
        this.addGhostNode({
          nodeId: null,
          _originalFileName: file.name,
          name: finalName,
          isFile: true,
          isFolder: false,
          content: { mimeType: file.type, sizeInBytes: file.size },
          properties: metadata || {},
          _ghost: true,
          _uploadedAt: Date.now(),
          _uploadContext: {
            parentNodeId: destinationNodeId || null,
            siteId: workspaceSiteId || null,
            activeViewId: null,
            activeViewConfig: null,
          },
        });
      }

      // Send only file metadata to worker (not the actual File objects)
      const uploadItems = files.map((file, index) => ({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        index,
      }));

      this.worker.postMessage({
        type: "startClassificationPlan",
        payload: {
          files: uploadItems,
        },
      });
    },

    /**
     * Start a classification plan upload where each file has its own
     * pathFull, metadata, and fileRenames.
     * @param {string} workspaceSiteId
     * @param {File[]} files
     * @param {Object[]} perFileData - Array of { pathFull, metadata, fileRenames } per file
     * @param {string} destinationNodeId
     */
    startUploadWithClassificationPlanMulti(
      workspaceSiteId,
      files,
      perFileData,
      destinationNodeId,
    ) {
      if (this.isUploading) {
        return;
      }

      this.initializeWorker();

      this.isUploading = true;
      this.progress = { completed: 0, total: files.length };

      this.files = files.map((f) => ({
        name: f.name,
        status: "pending",
        message: "",
        percent: 0,
      }));

      window.addEventListener("beforeunload", beforeUnloadHandler);

      // Store files with per-file data
      this.classificationPlanFiles = files.map((file, index) => ({
        file,
        workspaceSiteId,
        pathFull: perFileData[index].pathFull,
        metadata: perFileData[index].metadata,
        aspects: perFileData[index].aspects || [],
        fileRenames: perFileData[index].fileRenames,
        destinationNodeId,
        index,
      }));

      // Create ghost nodes immediately for all files
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const data = perFileData[i];
        const renameInfo = data.fileRenames?.find(
          (r) => r.original === file.name,
        );
        const finalName = renameInfo ? renameInfo.new : file.name;
        this.addGhostNode({
          nodeId: null,
          _originalFileName: file.name,
          name: finalName,
          isFile: true,
          isFolder: false,
          content: { mimeType: file.type, sizeInBytes: file.size },
          properties: data.metadata || {},
          _ghost: true,
          _uploadedAt: Date.now(),
          _uploadContext: {
            parentNodeId: destinationNodeId || null,
            siteId: workspaceSiteId || null,
            activeViewId: null,
            activeViewConfig: null,
          },
        });
      }

      const uploadItems = files.map((file, index) => ({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        index,
      }));

      this.worker.postMessage({
        type: "startClassificationPlan",
        payload: {
          files: uploadItems,
        },
      });
    },

    /**
     * Handle classification plan upload request from worker
     * Uses multipart/form-data for efficient upload without base64 encoding
     */
    async handleClassificationPlanUpload(item) {
      // Retrieve the full file info from stored classificationPlanFiles
      const storedItem = this.classificationPlanFiles[item.index];
      if (!storedItem) {
        console.error(
          `No stored file found for index ${item.index} (${item.fileName})`,
        );
        return;
      }

      const file = storedItem.file;
      const fileName = file.name;

      const fileState = this.files[item.index];
      if (fileState) {
        fileState.status = "uploading";
      }

      try {
        // Find rename mapping if exists
        const renameInfo = storedItem.fileRenames?.find(
          (r) => r.original === fileName,
        );
        const finalName = renameInfo ? renameInfo.new : fileName;

        // Create metadata object (everything except the file content)
        const pathRoot = storedItem.destinationNodeId
          ? `node:${storedItem.destinationNodeId}`
          : `site:${storedItem.workspaceSiteId}`;
        const metadata = {
          name: finalName,
          path: {
            root: pathRoot,
            full: storedItem.pathFull,
          },
          properties: storedItem.metadata,
        };

        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append("file", file, file.name);
        formData.append("fileSize", file.size.toString());
        formData.append("metadata", JSON.stringify(metadata));

        // Use axios with FormData - it automatically sets the correct Content-Type with boundary
        const response = await axios.post(
          "/alfresco/service/fr/jeci/pristy/nodes/inject",
          formData,
          {
            withCredentials: true, // Include cookies for authentication
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total && fileState) {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total,
                );
                fileState.percent = percentCompleted;
              }
            },
            headers: {
              Authorization: `Basic ${btoa(`ROLE_TICKET:${localStorage.getItem("ticket-ECM")}`)}`,
              Accept: "application/json",
            },
          },
        );

        if (fileState) {
          fileState.status = "success";
          fileState.percent = 100;
        }

        const entry = response.data?.entry || response.data;
        const nodeId = entry.id || entry.nodeId;

        // Apply aspects that may not be handled by the injector webscript
        if (storedItem.aspects?.length > 0 && nodeId) {
          try {
            const existingNode = await alfrescoNodeService.getNode(nodeId);
            const existingAspects = existingNode.aspectNames || [];
            const mergedAspects = [
              ...new Set([...existingAspects, ...storedItem.aspects]),
            ];
            await alfrescoNodeService.updateNode(nodeId, {
              aspectNames: mergedAspects,
            });
          } catch (aspectError) {
            console.error("Failed to apply aspects:", aspectError);
          }
        }

        // Update the pre-created ghost with real node ID and response data
        this.updateGhostAfterUpload(file.name, {
          nodeId: entry.id || entry.nodeId,
          name: finalName,
          content: entry.content || { mimeType: file.type },
          properties: {
            ...(storedItem.metadata || {}),
            ...(entry.properties || {}),
          },
        });

        this.worker.postMessage({
          type: "uploadResult",
          payload: {
            status: "fulfilled",
            value: response.data,
            fileName: fileName,
            index: item.index,
          },
        });
      } catch (error) {
        console.error(error);
        // Remove the pre-created ghost on upload failure
        this.removeGhostByTempKey(file.name);

        let parsedError;
        try {
          parsedError = errorService.uploadFileError(error, fileName);
        } catch (e) {
          parsedError = e;
        }

        if (fileState) {
          fileState.status = "error";
          fileState.message = parsedError.message || "Erreur inconnue";
        }

        this.worker.postMessage({
          type: "uploadResult",
          payload: {
            status: "rejected",
            reason: parsedError,
            fileName: fileName,
            index: item.index,
          },
        });
      }
    },
  },
});
